import { useEffect, useState } from "react";
import { Monitor, Smartphone, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmtQc = (iso) => {
  try {
    return new Intl.DateTimeFormat("fr-CA", { timeZone: "America/Toronto", dateStyle: "medium", timeStyle: "short" }).format(new Date(iso)) + " HE";
  } catch { return "—"; }
};
const flag = (cc) => (cc && cc.length === 2 ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))) : "");

export const SessionsPanel = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/auth/sessions").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const close = async (id) => {
    if (!window.confirm("Fermer cette session ? L'appareil devra se reconnecter.")) return;
    setBusy(id);
    try { await api.delete(`/auth/sessions/${id}`); toast.success("Session fermée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const closeOthers = async () => {
    if (!window.confirm("Fermer toutes les autres sessions ?")) return;
    setBusy("others");
    try { const { data } = await api.post("/auth/sessions/close-others"); toast.success(`${data.closed} session(s) fermée(s)`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!rows) return <div className="text-sm text-slate-400">Chargement…</div>;
  const others = rows.filter((r) => !r.current).length;
  return (
    <div className="space-y-3 max-w-3xl" data-testid="sessions-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600">Appareils actuellement connectés à votre compte. Fermez une session que vous ne reconnaissez pas.</p>
        <Button variant="outline" size="sm" disabled={!others || busy === "others"} onClick={closeOthers} data-testid="sessions-close-others-btn">
          {busy === "others" && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Fermer les autres sessions
        </Button>
      </div>
      {rows.map((r) => {
        const Icon = r.is_mobile ? Smartphone : Monitor;
        return (
          <div key={r.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3" data-testid="session-row">
            <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center"><Icon className="h-5 w-5 text-slate-600" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                {[r.browser, r.os].filter(Boolean).join(" · ") || r.device || "Appareil"}
                {r.current && <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5" data-testid="session-current-badge">Cet appareil</span>}
              </div>
              <div className="text-xs text-slate-500">{flag(r.country_code)} {[r.city, r.country].filter(Boolean).join(", ") || "Lieu inconnu"} · Connecté le {fmtQc(r.created_at)} · Actif {fmtQc(r.last_seen)}</div>
            </div>
            {!r.current && (
              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" disabled={busy === r.id} onClick={() => close(r.id)} data-testid={`session-close-${r.id}`}>
                {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4 mr-1" />}Fermer
              </Button>
            )}
          </div>
        );
      })}
      {rows.length === 0 && <div className="text-sm text-slate-400">Aucune session active.</div>}
    </div>
  );
};
